import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Heart, ChevronDown, Mail, Send } from "lucide-react";
import { Link } from "react-router-dom";

/* ─── Animated Section Wrapper ─── */
function FadeSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Gold Divider ─── */
function GoldDivider() {
  return <div className="gold-line w-24 mx-auto my-6" />;
}

/* ─── Wave Divider ─── */
function WaveDivider({ from, to }: { from: string; to: string }) {
  return (
    <div className="wave-divider" style={{ background: from }}>
      <svg viewBox="0 0 1440 120" preserveAspectRatio="none" style={{ display: "block" }}>
        <path
          d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z"
          fill={to}
        />
      </svg>
    </div>
  );
}

/* ─── Resource Images (Unsplash) ─── */
const IMAGES = {
  hospitalBag:
    "https://images.unsplash.com/photo-1512678080530-7760d81faba6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHw1fHxob3NwaXRhbCUyMGJpcnRoJTIwZGVsaXZlcnklMjByb29tJTIwd2FybXxlbnwwfDB8fHwxNzczMTY0MzU1fDA&ixlib=rb-4.1.0&q=80&w=1080",
  pregnancyYoga:
    "https://images.unsplash.com/photo-1680155216037-9a5d93ab6f67?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHxwcmVnbmFudCUyMHdvbWFuJTIwc3RyZXRjaGluZyUyMHlvZ2F8ZW58MHwwfHx8MTc3MzE2NDMwM3ww&ixlib=rb-4.1.0&q=80&w=1080",
  laborPrep:
    "https://images.unsplash.com/photo-1737098148863-65421ec92e65?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwzfHxsYWJvciUyMHByZXBhcmF0aW9uJTIwYmlydGglMjBwYXJ0bmVyJTIwc3VwcG9ydHxlbnwwfDB8fHwxNzczMTY0MzAzfDA&ixlib=rb-4.1.0&q=80&w=1080",
  breathing:
    "https://images.unsplash.com/photo-1758272422634-e8ed8e252a14?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHxicmVhdGhpbmclMjBleGVyY2lzZSUyMG1lZGl0YXRpb24lMjBjYWxtJTIwd29tYW58ZW58MHwwfHx8MTc3MzE2NDMwM3ww&ixlib=rb-4.1.0&q=80&w=1080",
  doulaSupport:
    "https://images.unsplash.com/photo-1543270216-7c25819fe5af?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwxfHxkb3VsYSUyMHN1cHBvcnQlMjBwcmVnbmFudCUyMHdvbWFuJTIwY29tZm9ydHxlbnwwfDB8fHwxNzczMTY0MzAzfDA&ixlib=rb-4.1.0&q=80&w=1080",
  postpartum:
    "https://images.unsplash.com/photo-1759802146987-e421a50476a1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3NTAxMTZ8MHwxfHNlYXJjaHwyfHxwb3N0cGFydHVtJTIwbW90aGVyJTIwbmV3Ym9ybiUyMGJhYnklMjBjYXJlfGVufDB8MHx8fDE3NzMxNjQzMDN8MA&ixlib=rb-4.1.0&q=80&w=1080",
};

/* ─── Accordion Resource Data ─── */
interface ResourceItem {
  title: string;
  preview: string;
  image: string;
  items: string[];
  note?: string;
}

const resources: ResourceItem[] = [
  {
    title: "What to Pack for Labor Day",
    preview: "Important items to prepare before heading to the hospital or birth center.",
    image: IMAGES.hospitalBag,
    items: [
      "ID and insurance card",
      "Comfortable robe or gown",
      "Nursing bra",
      "Toiletries",
      "Charger for your phone",
      "Slippers or comfortable socks",
      "Snacks and water bottle",
      "Birth plan copy",
      "Baby outfit",
      "Going-home outfit for mom",
      "Lip balm",
      "Hair ties",
      "Any comfort items that help you feel relaxed",
    ],
  },
  {
    title: "Simple Pregnancy Exercises",
    preview: "Gentle movement ideas that may help support your body during pregnancy.",
    image: IMAGES.pregnancyYoga,
    items: [
      "Walking",
      "Pelvic tilts",
      "Gentle stretching",
      "Hip circles on a yoga ball",
      "Cat-cow movement",
      "Posture and breathing exercises",
      "Deep squats only if approved by your provider",
    ],
    note: "Always consult your healthcare provider before starting any exercise routine during pregnancy.",
  },
  {
    title: "Signs Labor May Be Near",
    preview: "Common signs that may indicate your body is preparing for labor.",
    image: IMAGES.laborPrep,
    items: [
      "Baby dropping lower",
      "Increased pelvic pressure",
      "Stronger or more regular contractions",
      "Lower back discomfort",
      "Loss of mucus plug",
      "Water breaking",
      "Nesting energy",
      "Cervical changes",
    ],
    note: "If you think labor may be starting, contact your healthcare provider for guidance.",
  },
  {
    title: "Breathing and Relaxation Tips",
    preview: "Simple techniques to help you stay calm and grounded during labor.",
    image: IMAGES.breathing,
    items: [
      "Slow deep breathing",
      "Inhaling through the nose and exhaling through the mouth",
      "Relaxing the jaw and shoulders",
      "Using low vocal sounds",
      "Guided breathing with your partner",
      "Visualization and calming thoughts",
      "Resting between contractions",
      "Creating a peaceful environment",
    ],
  },
  {
    title: "How a Doula Can Support You",
    preview: "Emotional, physical, and educational support throughout pregnancy and birth.",
    image: IMAGES.doulaSupport,
    items: [
      "Emotional reassurance",
      "Birth preparation",
      "Comfort measures during labor",
      "Breathing and relaxation support",
      "Help guiding your partner",
      "Encouragement and calm presence",
      "Educational support",
      "Postpartum encouragement and care guidance",
    ],
  },
  {
    title: "Early Postpartum Recovery Tips",
    preview: "A few simple reminders for the first days after birth.",
    image: IMAGES.postpartum,
    items: [
      "Rest whenever possible",
      "Drink plenty of water",
      "Eat nourishing meals",
      "Accept help from loved ones",
      "Move gently as your body allows",
      "Ask for feeding support if needed",
      "Give yourself time to heal",
      "Prioritize emotional support and recovery",
    ],
  },
];

/* ─── Accordion Card Component ─── */
function AccordionCard({ resource, isOpen, onToggle, index }: {
  resource: ResourceItem;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}) {
  return (
    <FadeSection delay={index * 0.08}>
      <div
        className={`bg-white rounded-2xl overflow-hidden transition-shadow duration-500 cursor-pointer ${
          isOpen ? "shadow-xl ring-1 ring-mauve/10" : "shadow-md hover:shadow-lg"
        }`}
        onClick={onToggle}
      >
        {/* Top section: image + collapsed content */}
        <div className="grid md:grid-cols-[280px_1fr] items-stretch">
          {/* Image */}
          <div className="relative overflow-hidden aspect-[16/10] md:aspect-auto md:min-h-[180px]">
            <img
              src={resource.image}
              alt={resource.title}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>

          {/* Title + preview + arrow */}
          <div className="p-6 sm:p-8 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-taupe leading-snug mb-2">
                {resource.title}
              </h3>
              <p className="text-taupe/60 text-sm sm:text-base leading-relaxed">
                {resource.preview}
              </p>
            </div>

            {/* Arrow icon */}
            <motion.div
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="flex-shrink-0 mt-1 w-10 h-10 rounded-full bg-blush flex items-center justify-center"
            >
              <ChevronDown size={20} className="text-mauve" />
            </motion.div>
          </div>
        </div>

        {/* Expanded content */}
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="px-6 sm:px-8 pb-8 md:pl-[calc(280px+2rem)]">
                <div className="border-t border-blush pt-6">
                  <p className="text-taupe/75 text-sm sm:text-base leading-relaxed mb-4">
                    Preparing your hospital or birth center bag ahead of time can help you feel more calm and ready. Some essentials to consider:
                  </p>
                  <ul className="space-y-2.5">
                    {resource.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-taupe/80">
                        <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-mauve/50" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {resource.note && (
                    <div className="mt-6 p-4 bg-blush/50 rounded-xl border border-mauve/10">
                      <p className="text-sm text-mauve/90 italic leading-relaxed">{resource.note}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FadeSection>
  );
}

/* ══════════════════════════════════════════════════════ */
/* ═══            RESOURCES PAGE (was Blog)         ═══ */
/* ══════════════════════════════════════════════════════ */

export default function BlogPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="min-h-screen bg-ivory font-sans text-taupe">

      {/* ═══ HERO / HEADER ═══ */}
      <section className="relative bg-blush overflow-hidden">
        <div className="organic-blob w-80 h-80 bg-mauve -top-20 -right-20" />
        <div className="organic-blob w-64 h-64 bg-gold bottom-0 -left-16" />

        <div className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center relative z-10">
          <FadeSection>
            <p className="text-sm font-semibold text-mauve uppercase tracking-widest mb-4 flex items-center justify-center gap-2">
              <Heart size={14} className="text-mauve" />
              Blissful Arrival
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold text-taupe leading-tight">
              Helpful Resources
            </h1>
            <GoldDivider />
            <p className="text-lg sm:text-xl text-taupe/70 max-w-2xl mx-auto mt-4 leading-relaxed">
              Helpful information, simple preparation tips, and gentle guidance for expecting parents.
            </p>
          </FadeSection>
        </div>

        <WaveDivider from="#F2E9ED" to="#F8F5F3" />
      </section>

      {/* ═══ SECTION TITLE ═══ */}
      <section className="bg-ivory pt-16 pb-4">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeSection>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-taupe">
              Helpful Resources for Your Pregnancy Journey
            </h2>
            <GoldDivider />
            <p className="text-taupe/60 mt-2 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Helpful information, simple preparation tips, and gentle guidance for expecting parents.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* ═══ ACCORDION CARDS ═══ */}
      <section className="bg-ivory py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          {resources.map((resource, i) => (
            <AccordionCard
              key={i}
              resource={resource}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
              index={i}
            />
          ))}
        </div>
      </section>

      <WaveDivider from="#F8F5F3" to="#8C6675" />

      {/* ═══ CTA BANNER ═══ */}
      <section className="bg-mauve py-20 lg:py-24 relative overflow-hidden">
        <div className="organic-blob w-96 h-96 bg-white/10 -right-32 -top-20" />
        <div className="organic-blob w-72 h-72 bg-gold/15 -left-20 bottom-0" />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <FadeSection>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
              Need support right now?
            </h2>
            <GoldDivider />
            <p className="text-white/80 text-lg max-w-2xl mx-auto mt-4 mb-10 leading-relaxed">
              Schedule a free consultation and let's see how I can support you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-mauve font-semibold rounded-full hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                Free Consultation
              </Link>
              <Link
                to="/#insurance"
                className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white border-2 border-white/40 font-semibold rounded-full hover:bg-white/10 hover:border-white transition-all duration-300 hover:-translate-y-0.5"
              >
                Verify Insurance Coverage
              </Link>
            </div>
          </FadeSection>
        </div>
      </section>

      <WaveDivider from="#8C6675" to="#EFE3DB" />

      {/* ═══ NEWSLETTER ═══ */}
      <section className="bg-cream py-20 lg:py-24 relative overflow-hidden">
        <div className="organic-blob w-72 h-72 bg-mauve -right-16 top-0" />

        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <FadeSection>
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blush flex items-center justify-center mx-auto mb-6">
                <Mail size={28} className="text-mauve" />
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-taupe mb-3">
                Get new tips in your inbox
              </h2>
              <GoldDivider />
              <p className="text-taupe/65 mt-4 mb-8 leading-relaxed">
                Join our community of Sonoma County families. Receive helpful articles on pregnancy, birth, postpartum, and more — straight to your inbox.
              </p>

              <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    className="w-full px-5 py-3.5 rounded-xl border-2 border-blush bg-ivory/50 text-taupe placeholder:text-taupe/40 focus:border-mauve focus:ring-0 outline-none transition-colors"
                  />
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="w-full px-5 py-3.5 rounded-xl border-2 border-blush bg-ivory/50 text-taupe placeholder:text-taupe/40 focus:border-mauve focus:ring-0 outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full sm:w-auto group"
                >
                  Subscribe
                  <Send size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
                </button>
              </form>

              <p className="text-xs text-taupe/40 mt-4">
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </FadeSection>
        </div>
      </section>

      <WaveDivider from="#EFE3DB" to="#F8F5F3" />
    </div>
  );
}
